import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { bindActionCreators } from 'redux';
import { clientIdentification } from '@gjensidige/service-client-identification';
import { remoteLogMessage } from '@gjensidige/service-error-handler';

import boostActions from './actions/boostActions';
import * as eventCallbacks from './actions/callguideEventCallbackActions';
import requestCallbacks from './actions/callguideRequestCallbackActions';
import { toggleChatWindowVisible } from './actions/chatWindowVisibilityActions';
import webtrendsActions from './actions/webtrendsActions';
import { chatWindowActions } from './redux/slices/chat-window';
import { receiveConfig } from './redux/slices/config';
import { store } from './redux/store';
import { erInnlogget, hentTilganger } from './redux/thunks/bruker';
import {
  fetchChatAvailability,
  fetchChatOpeningHours
} from './redux/thunks/chat-window';
import { requestConfig } from './redux/thunks/config';
import callGuideService from './services/callguide-service';
import { proactiveService } from './services/proactive-web-service';
import { QueueStatus } from './types/api';
import { isCommercialContext } from './utils/customer-segment';
import { boostSession, chatSession } from './utils/session';

import styles from './css/app.module.less';

dayjs.extend(utc);
dayjs.extend(timezone);

const boundEventCallbacks = bindActionCreators(eventCallbacks, store.dispatch);
const boundRequestCallbacks = bindActionCreators(
  requestCallbacks,
  store.dispatch
);
const boundWebtrendsActions = bindActionCreators(
  webtrendsActions,
  store.dispatch
);
const boundToggleChatWindowVisible = bindActionCreators(
  toggleChatWindowVisible,
  store.dispatch
);
const boundBoostActions = bindActionCreators(boostActions, store.dispatch);

let waited = 0;
const maxWaitingTime = 10000;

chatSession.setDidRefresh(true);

// use this for public chat api.
window.CallGuideChatClient = { api: {} };

clientIdentification.configure({
  clientVersion: process.env.CLIENT_VERSION,
  clientId: process.env.CLIENT_NAME
});

const waitForWebSDK = window.setInterval(async () => {
  waited += 100;
  if (window.CallGuide?.api?.loaded) {
    window.clearInterval(waitForWebSDK);
    document.body.classList.add(styles.noOverlayScrollbar);

    // initialize config, load customer and tilganger and last, render app.
    store.dispatch(receiveConfig(window.ChatStickyConfig));

    if (window.ChatStickyConfig.config) {
      await store.dispatch(requestConfig(window.ChatStickyConfig.config));
    }

    if (window.ChatStickyConfig.defaultHiddenChatActivation) {
      store.dispatch(chatWindowActions.forceHideChatActivations());
    }

    proactiveService.init(
      boundRequestCallbacks,
      boundWebtrendsActions,
      boundToggleChatWindowVisible,
      boundBoostActions
    );
    callGuideService.init(
      boundEventCallbacks,
      boundRequestCallbacks,
      store.getState().config
    );

    const [innloggetRes, availabilityRes] = await Promise.all([
      store.dispatch(erInnlogget()),
      store.dispatch(fetchChatAvailability()),
      store.dispatch(fetchChatOpeningHours())
    ]);

    if (innloggetRes.payload && isCommercialContext()) {
      await store.dispatch(hentTilganger());
    }

    const chatAppEl = document.getElementById('chat-app');
    chatAppEl?.classList.add(styles.chatApp);

    const availablePayload = availabilityRes.payload as QueueStatus;

    if (isCommercialContext() && !availablePayload?.available) {
      // Prevent from display chat when non privat and unavailable
      return;
    }

    const { render } = await import('./app');

    render(chatAppEl);

    if (boostSession.exists()) {
      boundBoostActions.startRobotChat();
    }
  } else if (waited >= maxWaitingTime) {
    window.clearInterval(waitForWebSDK);
    void remoteLogMessage('Cant find CallGuide WebSDK Chat API', {
      severity: 'WARN',
      extras: [
        {
          key: 'location',
          value: window.location.pathname
        }
      ]
    });
  }
}, 100);

setTimeout(() => chatSession.setDidRefresh(false), 5000);
